import * as React from "react";
import { Archive, Award, Blocks, CalendarDays, CheckCircle2, Clock, Compass, Crown, FileText, FileUp, Files, Flame, Gem, GraduationCap, Hourglass, Layers, Library, Lightbulb, ListChecks, Lock, Medal, MessageCircle, MessagesSquare, Moon, RotateCcw, Shapes, Star, Sunrise, Target, Timer, TrendingUp, Trophy, Waves, Zap, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * One badge, drawn as a tiered medallion. The icon name comes from
 * public.achievements.icon; only icons the catalogue uses are imported so the
 * whole lucide set never lands in the bundle.
 */

const ICONS: Record<string, LucideIcon> = {
  // Badge rows may still name "Brain"; Novari no longer shows a brain icon.
  Brain: Target,
  Archive, Award, Blocks, CalendarDays, CheckCircle2, Clock, Compass, Crown,
  FileText, FileUp, Files, Flame, Gem, GraduationCap, Hourglass, Layers, Library,
  Lightbulb, ListChecks, Medal, MessageCircle, MessagesSquare, Moon, RotateCcw, Shapes,
  Sparkle: Star, Sparkles: Star, Star, Sunrise, Target, Timer, TrendingUp, Trophy, Waves, Zap,
};

export type BadgeTier = "bronze" | "silver" | "gold" | "legendary";

/** Metal colours are fixed (not theme tokens): a gold medal is gold in both themes. */
export const TIER_STYLE: Record<BadgeTier, { label: string; rim: [string, string, string]; face: [string, string]; ink: string; glow: string }> = {
  bronze: { label: "Bronze", rim: ["#8A4B22", "#E6A56B", "#9A5A2E"], face: ["#F4C79B", "#C9824C"], ink: "#5A2D10", glow: "rgba(201,130,76,0.45)" },
  silver: { label: "Silver", rim: ["#7C8794", "#F1F4F8", "#8B96A3"], face: ["#E9EDF2", "#AEB8C4"], ink: "#34404E", glow: "rgba(160,175,190,0.5)" },
  gold: { label: "Gold", rim: ["#A87A0C", "#FFE89A", "#B8860B"], face: ["#FFE7A0", "#E0A82E"], ink: "#5C3D00", glow: "rgba(234,179,8,0.5)" },
  legendary: { label: "Legendary", rim: ["#6D28D9", "#F0ABFC", "#DB2777"], face: ["#E9D5FF", "#C084FC"], ink: "#3B0764", glow: "rgba(192,132,252,0.55)" },
};

export function tierOf(tier?: string | null): BadgeTier {
  return tier === "silver" || tier === "gold" || tier === "legendary" ? tier : "bronze";
}

const SIZES = {
  sm: { box: 44, icon: 18 },
  md: { box: 64, icon: 26 },
  lg: { box: 88, icon: 36 },
  xl: { box: 132, icon: 54 },
} as const;

interface AchievementBadgeProps {
  icon?: string | null;
  tier?: string | null;
  unlocked?: boolean;
  size?: keyof typeof SIZES;
  /** 0–1 progress toward a locked badge; drawn as a ring. */
  progress?: number | null;
  className?: string;
  /** Accessible name; badges are decorative when omitted. */
  label?: string;
}

export function AchievementBadge({
  icon,
  tier: rawTier,
  unlocked = true,
  size = "md",
  progress,
  className,
  label,
}: AchievementBadgeProps) {
  const id = React.useId().replace(/:/g, "");
  const tier = tierOf(rawTier);
  const t = TIER_STYLE[tier];
  const { box, icon: iconSize } = SIZES[size];
  const Icon = (icon && ICONS[icon]) || Trophy;
  const showRing = !unlocked && progress != null && progress > 0;
  const ring = Math.max(0, Math.min(1, progress ?? 0));
  const r = 47;
  const circ = 2 * Math.PI * r;

  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn("relative inline-grid shrink-0 place-items-center", className)}
      style={{ width: box, height: box }}
    >
      <svg viewBox="0 0 100 100" width={box} height={box} className="absolute inset-0">
        <defs>
          <linearGradient id={`rim-${id}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={t.rim[0]} />
            <stop offset="45%" stopColor={t.rim[1]} />
            <stop offset="100%" stopColor={t.rim[2]} />
          </linearGradient>
          <radialGradient id={`face-${id}`} cx="35%" cy="30%" r="75%">
            <stop offset="0%" stopColor={t.face[0]} />
            <stop offset="100%" stopColor={t.face[1]} />
          </radialGradient>
          <linearGradient id={`shine-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {unlocked ? (
          <>
            {/* Scalloped outer edge reads as a medal, not a plain circle. */}
            <g fill={`url(#rim-${id})`}>
              {Array.from({ length: 16 }, (_, i) => {
                const a = (i / 16) * Math.PI * 2;
                return <circle key={i} cx={50 + Math.cos(a) * 42} cy={50 + Math.sin(a) * 42} r={8} />;
              })}
              <circle cx="50" cy="50" r="44" />
            </g>
            <circle cx="50" cy="50" r="36" fill={`url(#face-${id})`} />
            <circle cx="50" cy="50" r="36" fill="none" stroke={t.rim[0]} strokeOpacity="0.35" strokeWidth="1.5" />
            <ellipse cx="50" cy="32" rx="26" ry="14" fill={`url(#shine-${id})`} />
          </>
        ) : (
          <>
            <circle cx="50" cy="50" r="44" fill="var(--bg-surface-2)" />
            <circle cx="50" cy="50" r="44" fill="none" stroke="var(--border-strong)" strokeWidth="2" strokeDasharray="4 5" />
            {showRing && (
              <circle
                cx="50"
                cy="50"
                r={r}
                fill="none"
                stroke="var(--accent-primary)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${circ * ring} ${circ}`}
                transform="rotate(-90 50 50)"
              />
            )}
          </>
        )}
      </svg>

      <Icon
        className="relative"
        style={{ width: iconSize, height: iconSize, color: unlocked ? t.ink : "var(--text-muted)" }}
        strokeWidth={unlocked ? 2.25 : 2}
        aria-hidden="true"
      />

      {!unlocked && (
        <span
          className="absolute -bottom-0.5 -right-0.5 grid place-items-center rounded-full bg-surface text-ink-3 shadow-ledge"
          style={{ width: Math.max(18, box * 0.3), height: Math.max(18, box * 0.3) }}
        >
          <Lock style={{ width: Math.max(10, box * 0.15), height: Math.max(10, box * 0.15) }} aria-hidden="true" />
        </span>
      )}
    </span>
  );
}

export function TierLabel({ tier, className }: { tier?: string | null; className?: string }) {
  const t = TIER_STYLE[tierOf(tier)];
  return (
    <span
      className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-caption font-semibold", className)}
      style={{ background: `linear-gradient(135deg, ${t.face[0]}, ${t.face[1]})`, color: t.ink }}
    >
      {t.label}
    </span>
  );
}

export default AchievementBadge;
